import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, MapPin, Clock, ShieldCheck, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { unitSuffix } from "@/lib/pricing";

export default function ServiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [svc, setSvc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/services/${id}`).then(({ data }) => setSvc(data)).finally(() => setLoading(false));
  }, [id]);

  const bookNow = () => {
    if (!user) return nav("/login", { state: { from: `/booking/${id}` } });
    if (user.role !== "customer") return nav("/login");
    nav(`/booking/${id}`);
  };

  if (loading) return <div className="py-16 text-center text-sm text-muted-foreground">Loading service...</div>;
  if (!svc) return <div className="py-16 text-center text-sm text-muted-foreground">Service not found.</div>;

  return (
    <div className="space-y-6" data-testid="service-detail-page">
      <Link to="/browse" className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground">
        <ChevronLeft size={14} /> Back to browse
      </Link>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="overflow-hidden rounded-3xl border">
            <img src={svc.image_url || svc.category?.image_url} alt={svc.title} className="aspect-[16/9] w-full object-cover" />
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">{svc.category?.name}</div>
            <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="service-title">
              {svc.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Star size={14} className="fill-brand-yellow text-brand-yellow" /> {svc.provider?.rating_avg || "New"} · {svc.provider?.rating_count || 0} reviews</span>
              <span className="flex items-center gap-1"><Clock size={14} /> {svc.duration_minutes} min</span>
              {svc.location && <span className="flex items-center gap-1"><MapPin size={14} /> {svc.location}</span>}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-foreground/80">{svc.description}</p>
          </div>

          {/* Provider */}
          <div className="rounded-2xl border p-5">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Your provider</div>
            <div className="mt-3 flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-brand-ink font-heading text-lg font-bold text-white">
                {svc.provider?.name?.[0] || "P"}
              </div>
              <div>
                <div className="font-heading text-base font-bold">{svc.provider?.name}</div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck size={12} className="text-brand-orange" /> KYC verified
                </div>
              </div>
            </div>
          </div>

          {/* Reviews */}
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight">Reviews</h2>
            {(!svc.reviews || svc.reviews.length === 0) ? (
              <div className="mt-3 rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">No reviews yet.</div>
            ) : (
              <div className="mt-3 space-y-3">
                {svc.reviews.map((r) => (
                  <div key={r.id} className="rounded-2xl border p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">{r.customer?.name || "Customer"}</div>
                      <div className="flex items-center gap-1 text-xs font-semibold">
                        <Star size={12} className="fill-brand-yellow text-brand-yellow" /> {r.rating}
                      </div>
                    </div>
                    {r.comment && <div className="mt-1 text-sm text-foreground/80">{r.comment}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sticky booking card */}
        <div>
          <div className="rounded-3xl border border-foreground/10 bg-white p-6 lg:sticky lg:top-24">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Starting from</div>
            <div className="mt-1 font-heading text-4xl font-extrabold tracking-tight">
              ₹{svc.price}
              {svc.pricing_unit && svc.pricing_unit !== "flat" && (
                <span className="ml-1 text-lg font-bold text-muted-foreground">{unitSuffix(svc.pricing_unit)}</span>
              )}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">All-inclusive · UPI payment · Instant confirm</div>
            <Button
              onClick={bookNow}
              className="mt-6 h-12 w-full rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
              data-testid="book-now-btn"
            >
              Book now
            </Button>
            <div className="mt-4 space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-2"><ShieldCheck size={12} className="text-brand-orange" /> Free cancellation before service</div>
              <div className="flex items-center gap-2"><Star size={12} className="text-brand-orange" /> Satisfaction guarantee</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
